import React, { useId } from 'react';

import { FieldSet, FormField, FormHelp, Heading, Label, LabeledControl, Legend, Radio } from '@zillow/constellation-10';

import type { Section } from '../../../models';
import { FIRE_SAFETY_AND_PROTECTION } from '../../../constants';
import YesAndNo from '../../Widgets/YesAndNo';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

function FireSafetyAndProtection({ section, setSectionValues }: Props) {
  const radioGroupName = useId();
  const batteryOperatedId = useId();
  const hardWiredId = useId();
  const bothId = useId();

  function onBatteryOperatedSelect(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED]: true,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.HARD_WIRED]: false,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED_AND_HARD_WIRED]: false,
    });
  }

  function onHardWiredSelect(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED]: false,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.HARD_WIRED]: true,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED_AND_HARD_WIRED]: false,
    });
  }

  function onBatteryOperatedAndHardWiredSelect(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED]: false,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.HARD_WIRED]: false,
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED_AND_HARD_WIRED]: true,
    });
  }

  function onYesFireSprinkler(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_SPRINKLER_SYSTEM]: true,
    });
  }

  function onNoFireSprinkler(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_SPRINKLER_SYSTEM]: false,
    });
  }

  function onYesFireAlarm(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_ALARM_SYSTEM]: true,
    });
  }

  function onNoFireAlarm(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_ALARM_SYSTEM]: false,
    });
  }

  function onYesSmokingPolicy(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.SMOKING_POLICY]: true,
    });
  }

  function onNoSmokingPolicy(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.SMOKING_POLICY]: false,
    });
  }

  function onYesEmergencyNotificationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_NOTIFICATION_PLAN]: true,
    });
  }

  function onNoEmergencyNotificationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_NOTIFICATION_PLAN]: false,
    });
  }

  function onYesEmergencyRelocationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_RELOCATION_PLAN]: true,
    });
  }

  function onNoEmergencyRelocationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_RELOCATION_PLAN]: false,
    });
  }
  function onYesEmergencyEvacuationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_EVACUATION_PLAN]: true,
    });
  }

  function onNoEmergencyEvacuationPlan(): void {
    setSectionValues({
      [FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_EVACUATION_PLAN]: false,
    });
  }

  return (
    <>
      <Heading level={3} textStyle="responsive.heading-xs" css={{ marginBottom: 'layout.tight' }}>
        What are the fire safety and protection details for the property?
      </Heading>
      <FieldSet
        required
        legend={<Legend>How are the smoke detectors powered?</Legend>}
        css={{ marginBottom: 'layout.tight' }}
      >
        <LabeledControl
          controlId={batteryOperatedId}
          label={<Label>Battery operated</Label>}
          control={
            <Radio
              name={radioGroupName}
              checked={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED)}
              onChange={onBatteryOperatedSelect}
            />
          }
        />
        <LabeledControl
          controlId={hardWiredId}
          label={<Label>Hard-wired</Label>}
          control={
            <Radio
              name={radioGroupName}
              checked={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.HARD_WIRED)}
              onChange={onHardWiredSelect}
            />
          }
        />
        <LabeledControl
          controlId={bothId}
          label={<Label>Some battery operated, some hard-wired</Label>}
          control={
            <Radio
              name={radioGroupName}
              checked={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.BATTERY_POWERED_AND_HARD_WIRED)}
              onChange={onBatteryOperatedAndHardWiredSelect}
            />
          }
        />
      </FieldSet>
      <FormField
        required
        label={<Label>Does the building have a fire sprinkler system?</Label>}
        css={{ marginBottom: 'layout.tight' }}
        control={
          <YesAndNo
            yesHandler={onYesFireSprinkler}
            noHandler={onNoFireSprinkler}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_SPRINKLER_SYSTEM)}
          />
        }
      />
      <FormField
        required
        label={<Label>Does the building have a fire alarm system?</Label>}
        css={{ marginBottom: 'layout.tight' }}
        control={
          <YesAndNo
            yesHandler={onYesFireAlarm}
            noHandler={onNoFireAlarm}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.FIRE_ALARM_SYSTEM)}
          />
        }
      />
      <FormField
        required
        label={<Label>Does the building have a smoking policy?</Label>}
        css={{ marginBottom: 'layout.tight' }}
        control={
          <YesAndNo
            yesHandler={onYesSmokingPolicy}
            noHandler={onNoSmokingPolicy}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.SMOKING_POLICY)}
          />
        }
        description={
          <FormHelp>Later on, you will have the ability to describe smoking policies for your property</FormHelp>
        }
      />
      <FormField
        required
        label={<Label>Does the building have a emergency notification plan?</Label>}
        css={{ marginBottom: 'layout.tight' }}
        control={
          <YesAndNo
            yesHandler={onYesEmergencyNotificationPlan}
            noHandler={onNoEmergencyNotificationPlan}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_NOTIFICATION_PLAN)}
          />
        }
        description={
          <FormHelp>
            If the building has an emergency notification plan, you must provide the tenant with a copy before the lease
            is signed by tenant. Later on, you will have the ability to attach any reports to your lease.
          </FormHelp>
        }
      />
      <FormField
        required
        label={<Label>Does the building have a emergency relocation plan?</Label>}
        css={{ marginBottom: 'layout.tight' }}
        control={
          <YesAndNo
            yesHandler={onYesEmergencyRelocationPlan}
            noHandler={onNoEmergencyRelocationPlan}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_RELOCATION_PLAN)}
          />
        }
        description={
          <FormHelp>
            If the building has an emergency relocation plan, you must provide the tenant with a copy before the lease
            is signed by tenant. Later on, you will have the ability to attach any reports to your lease.
          </FormHelp>
        }
      />
      <FormField
        required
        label={<Label>Does the building have a emergency evacuation plan?</Label>}
        control={
          <YesAndNo
            yesHandler={onYesEmergencyEvacuationPlan}
            noHandler={onNoEmergencyEvacuationPlan}
            yesActive={section.getFieldValue(FIRE_SAFETY_AND_PROTECTION.FIELD_NAME.EMERGENCY_EVACUATION_PLAN)}
          />
        }
        description={
          <FormHelp>
            If the building has an emergency evacuation plan, you must provide the tenant with a copy before the lease
            is signed by tenant. Later on, you will have the ability to attach any reports to your lease.
          </FormHelp>
        }
      />
    </>
  );
}

FireSafetyAndProtection.description = 'Asks about fire safety features within a building for Washington state';

export default FireSafetyAndProtection;
